/**
 * Dashboard Page
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import type {
  ApiKey,
  CostSummary,
  MostCalledFunction,
  MemoryStatsSummary,
  SystemHealthSummary,
} from '../api/types';
import { Card, CardContent, CardHeader, CardTitle, StatCard } from '../components/Card';
import { Badge } from '../components/Table';
import {
  Key,
  Activity,
  DollarSign,
  AlertTriangle,
  ArrowRight,
  Database,
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  Bug,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export function DashboardPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalKeys: 0,
    activeKeys: 0,
    totalCost: 0,
    totalCalls: 0,
  });
  const [recentKeys, setRecentKeys] = useState<ApiKey[]>([]);
  const [topFunctions, setTopFunctions] = useState<MostCalledFunction[]>([]);
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [memoryStats, setMemoryStats] = useState<MemoryStatsSummary | null>(null);
  const [healthSummary, setHealthSummary] = useState<SystemHealthSummary | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load data in parallel
      const [keysRes, functionsRes, costsRes, memRes, healthRes] = await Promise.all([
        apiClient.listKeys({ limit: 100 }),
        apiClient.getMostCalledFunctions({ limit: 5 }),
        apiClient.getCostSummary(),
        apiClient.getMemoryStatsSummary().catch(() => null),
        apiClient.getSystemHealthSummary().catch(() => null),
      ]);

      // Calculate stats
      const activeKeys = keysRes.keys.filter((k) => k.isActive);
      const totalCalls = functionsRes.functions.reduce((sum, f) => sum + f.callCount, 0);

      setStats({
        totalKeys: keysRes.count,
        activeKeys: activeKeys.length,
        totalCost: costsRes.summary.totalCost,
        totalCalls,
      });

      setRecentKeys(keysRes.keys.slice(0, 5));
      setTopFunctions(functionsRes.functions);
      setCostSummary(costsRes.summary);
      if (memRes?.success) setMemoryStats(memRes.summary);
      if (healthRes?.success) setHealthSummary(healthRes.summary);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare chart data
  const functionChartData = topFunctions.map((f) => ({
    name: f.functionName.replace('_', ' '),
    calls: f.callCount,
    errorRate: Math.round(f.errorRate * 100),
  }));

  const costChartData = costSummary
    ? Object.entries(costSummary.byFunction).map(([name, data]) => ({
        name: name.replace('_', ' '),
        cost: Number(data.cost.toFixed(4)),
        calls: data.calls,
      }))
    : [];

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your vg-mem instance</p>
      </div>

      {/* System Health Banner */}
      {healthSummary && (
        <Link to="/health">
          <div
            className={`rounded-xl p-4 cursor-pointer transition-opacity hover:opacity-90 ${
              healthSummary.currentStatus === 'healthy'
                ? 'bg-green-50 border border-green-200'
                : healthSummary.currentStatus === 'degraded'
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge
                  variant={
                    healthSummary.currentStatus === 'healthy'
                      ? 'success'
                      : healthSummary.currentStatus === 'degraded'
                        ? 'warning'
                        : 'danger'
                  }
                  className="inline-flex items-center gap-1"
                >
                  {healthSummary.currentStatus === 'healthy' ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : healthSummary.currentStatus === 'degraded' ? (
                    <Clock className="w-3 h-3" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  {healthSummary.currentStatus.charAt(0).toUpperCase() +
                    healthSummary.currentStatus.slice(1)}
                </Badge>
                <span className="text-gray-700">
                  Uptime: <strong>{healthSummary.uptimePercent.toFixed(1)}%</strong>
                </span>
                <span className="text-gray-500">|</span>
                <span className="text-gray-700">
                  Neo4j: <strong>{healthSummary.avgNeo4jLatencyMs.toFixed(0)}ms</strong>
                </span>
                <span className="text-gray-700">
                  Qdrant: <strong>{healthSummary.avgQdrantLatencyMs.toFixed(0)}ms</strong>
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                {healthSummary.recentErrors > 0 && (
                  <span className="text-red-600 flex items-center gap-1">
                    <Bug className="w-4 h-4" />
                    {healthSummary.recentErrors} errors
                  </span>
                )}
                {healthSummary.activeAlerts > 0 && (
                  <span className="text-yellow-600 flex items-center gap-1">
                    <Bell className="w-4 h-4" />
                    {healthSummary.activeAlerts} alerts
                  </span>
                )}
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Memory Stats */}
      {memoryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Entities"
            value={memoryStats.totalEntities.toLocaleString()}
            subtitle={
              memoryStats.entityGrowth !== 0
                ? `${memoryStats.entityGrowth > 0 ? '+' : ''}${memoryStats.entityGrowth} today`
                : 'No change today'
            }
            icon={<Database className="w-6 h-6" />}
          />
          <StatCard
            title="Total Facts"
            value={memoryStats.totalFacts.toLocaleString()}
            subtitle={`${memoryStats.activeFacts.toLocaleString()} active`}
            icon={<Database className="w-6 h-6" />}
          />
          <StatCard
            title="Total Episodes"
            value={memoryStats.totalEpisodes.toLocaleString()}
            subtitle={`${memoryStats.todayIngestions} ingested today`}
            icon={<Activity className="w-6 h-6" />}
          />
          <StatCard
            title="Today's Activity"
            value={memoryStats.todaySearches.toLocaleString()}
            subtitle="searches performed"
            icon={<Activity className="w-6 h-6" />}
          />
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total API Keys"
          value={stats.totalKeys}
          subtitle={`${stats.activeKeys} active`}
          icon={<Key className="w-6 h-6" />}
        />
        <StatCard
          title="Active Keys"
          value={stats.activeKeys}
          icon={<Activity className="w-6 h-6" />}
        />
        <StatCard
          title="Total Cost"
          value={`$${stats.totalCost.toFixed(4)}`}
          subtitle="All time"
          icon={<DollarSign className="w-6 h-6" />}
        />
        <StatCard
          title="Total API Calls"
          value={stats.totalCalls.toLocaleString()}
          icon={<Activity className="w-6 h-6" />}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Functions Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Most Called Functions</CardTitle>
          </CardHeader>
          <CardContent>
            {functionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={functionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost by Function Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Cost by Function</CardTitle>
          </CardHeader>
          <CardContent>
            {costChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={costChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value}`} />
                  <Bar dataKey="cost" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent API Keys */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent API Keys</CardTitle>
          <Link
            to="/keys"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-200">
            {recentKeys.length > 0 ? (
              recentKeys.map((key) => (
                <div
                  key={key.uuid}
                  className="px-6 py-4 flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-900">{key.name}</p>
                    <p className="text-sm text-gray-500">
                      {key.keyPrefix}... | {key.tenantId}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">
                      {key.requestCount} requests
                    </span>
                    <Badge variant={key.isActive ? 'success' : 'danger'}>
                      {key.isActive ? 'Active' : 'Revoked'}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center text-gray-500">
                No API keys created yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Function Performance */}
      {topFunctions.some((f) => f.errorRate > 0.1) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">
                  High Error Rates Detected
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Some functions have error rates above 10%. Check the Metrics
                  page for details.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
